import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable } from "@dnd-kit/sortable";
import { getEventCoordinates } from "@dnd-kit/utilities";
import {
  Box,
  Button,
  DND_DRAGGABLE_PROPS,
  DND_OVERLAY_PROPS,
  DND_TOUCH_ACTIVATION,
  Icon,
  Image,
  sortableHandleProps,
  SPRING_DROP_ANIMATION,
} from "@salestrak/ui";
import {
  MEDIA_TILE_SIZE,
  REMOVE_BUTTON_OVERHANG,
  UploadingMediaTile,
  VideoPlayBadge,
} from "#app/components/media/media-tiles";
import {
  MediaAttachmentTile,
  type NoteMediaItem,
} from "#app/components/media/media-attachment-tile";
import { MediaFileCard } from "#app/components/media/media-file-card";
import { useMediaObjectUrl } from "#app/lib/hooks/use-note-media";
import { hasThumbnail, mediaKind } from "#app/lib/media-kind";
import type { PendingMediaUpload } from "#app/lib/hooks/use-pending-media-uploads";
import type { Omittable } from "@salestrak/ui";

// Thumbnail side length. The composer's remove button (24px, size xs)
// straddles the top-right corner — half in, half out — so removable cards
// reserve headroom via the row's padding/gap, not the tile itself.
const TILE_SIZE = MEDIA_TILE_SIZE;
// Row gap in px (gap="sm", matching NoteMediaDisplay) — the drop indicator line centers inside it.
const ROW_GAP_PX = 8;
const INDICATOR_WIDTH = 3;
const INDICATOR_OFFSET = -(ROW_GAP_PX / 2) - Math.floor(INDICATOR_WIDTH / 2);

// Where the drag would insert: in the gap on one side of a tile.
interface DropTarget {
  overId: string;
  side: "before" | "after";
}

// Resolve the drop target from the POINTER only. The rect-based detectors
// key off the dragged/overlay geometry, which misresolves in this wrapping
// row (drops always landed at the end). Pointer-in-tile wins; pointer in a
// gap falls to the tile whose center is nearest the pointer.
const pointerCollision: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  if (within.length) return within;

  const { pointerCoordinates, droppableContainers, droppableRects } = args;
  if (!pointerCoordinates) return [];
  let bestId: string | null = null;
  let bestDistance = Infinity;
  for (const container of droppableContainers) {
    const rect = droppableRects.get(container.id);
    if (!rect) continue;
    const dx = rect.left + rect.width / 2 - pointerCoordinates.x;
    const dy = rect.top + rect.height / 2 - pointerCoordinates.y;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      bestId = String(container.id);
    }
  }
  return bestId ? [{ id: bestId }] : [];
};

export type NoteMediaCard = Omittable<{
  photoId: string;
  /** Local preview object URL — image display blob or PDF/video thumb; null for
   * documents (the card shows a file icon) and for already-posted media, whose
   * thumb resolves from the server. */
  uri: string | null;
  contentType: string;
  fileName?: string | null;
}>;

type NotePhotoAttachmentsProps = Omittable<{
  photos: NoteMediaCard[];
  /** In-flight uploads, shown as progress tiles trailing the committed cards. */
  pending?: PendingMediaUpload[];
  /** When set, each card gets a trash button — hover-revealed with a
   * pointer, always visible on touch devices. */
  onRemove?: (photoId: string) => void;
  /** When set, each pending tile gets a cancel button that aborts its upload. */
  onCancelPending?: (tempId: string) => void;
  /** When set (desktop pointer only), tiles are drag-sortable; called with
   * the full photoId list in its new order. */
  onReorder?: (mediaIds: string[]) => void;
}>;

/**
 * Posted-note variant: renders each attachment by media class (image/pdf/video
 * thumbnail tile or document file card) via MediaAttachmentTile. `onImageClick`
 * fires only for IMAGE tiles (the lightbox is image-only) with the image's id.
 */
export function NoteMediaDisplay({
  media,
  onImageClick,
}: Omittable<{
  media: NoteMediaItem[];
  onImageClick?: (imageId: string) => void;
}>) {
  if (!media.length) return null;
  return (
    <Box direction="row" wrap gap="sm">
      {media.map((item, index) => (
        <MediaAttachmentTile
          key={`${item.id}-${index}`}
          item={item}
          onImageClick={onImageClick ? () => onImageClick(item.id) : undefined}
        />
      ))}
    </Box>
  );
}

/**
 * Footer-attached note photos as a wrapping row of thumbnail cards (composer
 * variant). Hovering a card outlines it in primary; the trash button sits
 * half outside the top-right corner. With `onReorder`, tiles drag-sort
 * (desktop pointer only — same constraint as the settings rows).
 *
 * Tiles stay static while dragging; a primary vertical line marks the
 * insertion gap. The target snaps by pointer halves: left half of a tile
 * inserts BEFORE it, right half AFTER it — recomputed on every drag move.
 */
export function NoteMediaAttachments({
  photos,
  pending,
  onRemove,
  onCancelPending,
  onReorder,
}: NotePhotoAttachmentsProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);
  // Drop uses the same target the indicator showed — kept in a ref so
  // handleDragEnd never races the last setTarget render.
  const targetRef = useRef<DropTarget | null>(null);

  // Distance threshold keeps plain clicks (trash button) from starting drags;
  // a finger drags after a hold, so a swipe over the tiles still scrolls.
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 8 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: DND_TOUCH_ACTIVATION,
  });
  const sortable = !!onReorder;
  const sensors = useSensors(
    ...(sortable ? [mouseSensor, touchSensor] : []),
  );

  const pendingTiles = (pending ?? []).map((p) => (
    <UploadingMediaTile
      key={p.tempId}
      uri={p.uri}
      progress={p.progress}
      onCancel={onCancelPending ? () => onCancelPending(p.tempId) : undefined}
    />
  ));

  if (!photos.length) {
    // Only uploads in flight (no committed cards yet): show the progress
    // tiles in a plain row — no dnd context needed.
    return pendingTiles.length ? (
      <Box direction="row" wrap gap="sm" paddingRight="xs">
        {pendingTiles}
      </Box>
    ) : null;
  }

  const ids = photos.map((p) => p.photoId);
  const activeIndex = activeId ? ids.indexOf(activeId) : -1;

  // Insertion-as-arrayMove: `to` is the dragged item's index AFTER removal.
  const resolveMove = (
    drop: DropTarget,
  ): { from: number; to: number } | null => {
    const from = activeIndex;
    const overIndex = ids.indexOf(drop.overId);
    if (from < 0 || overIndex < 0) return null;
    if (overIndex === from) return null;
    if (drop.side === "before") {
      if (overIndex === from + 1) return null; // already there
      return { from, to: overIndex < from ? overIndex : overIndex - 1 };
    }
    if (overIndex === from - 1) return null; // already there
    return { from, to: overIndex < from ? overIndex + 1 : overIndex };
  };

  const indicatorFor = (photoId: string): "before" | "after" | null => {
    if (!target || target.overId !== photoId) return null;
    return resolveMove(target) ? target.side : null;
  };

  const updateTarget = (next: DropTarget | null) => {
    targetRef.current = next;
    setTarget(next);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  // Fires on every pointer move — `over` alone only changes per tile, but
  // the before/after half can flip while over the same tile.
  const handleDragMove = (event: DragMoveEvent) => {
    const { over, activatorEvent, delta } = event;
    if (!over) {
      updateTarget(null);
      return;
    }
    const start = getEventCoordinates(activatorEvent);
    if (!start) {
      updateTarget(null);
      return;
    }
    const pointerX = start.x + delta.x;
    const side =
      pointerX < over.rect.left + over.rect.width / 2 ? "before" : "after";
    updateTarget({ overId: String(over.id), side });
  };

  const reset = () => {
    setActiveId(null);
    updateTarget(null);
  };

  const handleDragEnd = () => {
    const drop = targetRef.current;
    reset();
    if (!drop || !onReorder) return;
    const move = resolveMove(drop);
    if (!move) return;
    onReorder(arrayMove(ids, move.from, move.to));
  };

  const activePhoto = activeId
    ? photos.find((p) => p.photoId === activeId)
    : null;

  const row = (
    <Box direction="row" wrap gap="sm" paddingRight="xs">
      {photos.map((photo) => (
        <SortableTile
          key={photo.photoId}
          photo={photo}
          sortable={sortable}
          dimmed={photo.photoId === activeId}
          // Mid-drag, the row is in "placement" mode: the indicator line is
          // the only affordance — no hover borders, no trash buttons.
          dragInProgress={activeId != null}
          indicator={indicatorFor(photo.photoId)}
          onRemove={onRemove}
        />
      ))}
      {/* In-flight uploads trail the committed cards; static (not sortable
          until they land and join `photos`). */}
      {pendingTiles}
    </Box>
  );

  if (!sortable) return row;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerCollision}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <SortableContext items={ids}>{row}</SortableContext>
      {/* Portalled to the body: DragOverlay positions with fixed viewport
          coordinates, and a transformed ancestor (the issue-view sheet)
          re-roots them — the ghost then trails the pointer and slides in
          from the side on drop. */}
      {createPortal(
        <DragOverlay dropAnimation={SPRING_DROP_ANIMATION}>
          {activePhoto ? (
            <Box {...DND_OVERLAY_PROPS}>
              <TileImage card={activePhoto} hoverable={false} />
            </Box>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

function SortableTile({
  photo,
  sortable,
  dimmed,
  dragInProgress,
  indicator,
  onRemove,
}: Omittable<{
  photo: NoteMediaCard;
  sortable: boolean;
  dimmed: boolean;
  dragInProgress: boolean;
  indicator: "before" | "after" | null;
  onRemove?: (photoId: string) => void;
}>) {
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id: photo.photoId, disabled: !sortable });

  return (
    <Box
      group="tile" ref={setNodeRef}
      position="relative"
      width={TILE_SIZE}
      height={TILE_SIZE}
      cursor={!sortable ? undefined : isDragging ? "grabbing" : "grab"}
      // The tile is the handle and sits in a scrolling feed, so it keeps
      // panning; the hold-activated touch sensor prevents the scroll once a
      // hold completes.
      touchAction={sortable ? "manipulation" : undefined}
      {...(sortable ? sortableHandleProps(attributes, listeners) : undefined)}
      {...(sortable ? DND_DRAGGABLE_PROPS : undefined)}
    >
      <Box className={dimmed ? "opacity-30" : undefined}>
        <TileImage card={photo} hoverable={!dragInProgress} />
      </Box>
      {indicator ? (
        <Box
          pointerEvents="none" position="absolute"
          top={0}
          bottom={0}
          {...(indicator === "before"
            ? { left: INDICATOR_OFFSET }
            : { right: INDICATOR_OFFSET })}
          width={INDICATOR_WIDTH}
          rounded="full"
          background="primary"
        />
      ) : null}
      {onRemove && !dragInProgress ? (
        <Box
          hoverReveal="tile" position="absolute"
          top={REMOVE_BUTTON_OVERHANG}
          right={REMOVE_BUTTON_OVERHANG}
          // Straddles the corner into the gap, so it must paint over the
          // next tile (later siblings otherwise stack on top).
          zIndex={1}
          // Hover-revealed with a pointer; on a coarse pointer the tile's
          // first tap arms it and shows the button — CSS, not a JS touch check.
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Button
            size="icon-xs"
            onClick={() => onRemove(photo.photoId)}
            aria-label="Remove photo"
          >
            <Icon name="trash" size="xs" />
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

// The clipped media box — shared by in-row tiles and the drag overlay ghost.
// Images and PDF/video (with a preview thumb) show the thumbnail; documents
// (no thumb) show a file card. Video tiles get a play badge.
function TileImage({ card, hoverable }: { card: NoteMediaCard; hoverable: boolean }) {
  const kind = mediaKind(card.contentType);
  const resolved = useMediaObjectUrl(
    !card.uri && hasThumbnail(card.contentType) ? card.photoId : "",
    "thumb",
  );
  const uri = card.uri ?? resolved;
  if (kind === "file" || !uri) {
    return (
      <MediaFileCard
        contentType={card.contentType}
        fileName={card.fileName}
        size={TILE_SIZE}
      />
    );
  }
  return (
    <Box
      position="relative"
      width={TILE_SIZE}
      height={TILE_SIZE}
      rounded="md"
      overflow="hidden"
      border
      surface="card"
      className={hoverable ? "hover:border-primary" : undefined}
    >
      <Image src={uri} fill fit="cover" alt="Note media" />
      {kind === "video" ? <VideoPlayBadge /> : null}
    </Box>
  );
}
